/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import uniqBy from 'lodash/uniqBy';
import isEqual from 'lodash/isEqual';
import ActionCreator from '../action-creator';
import {
  SortDirection,
  DetailsPanelGroupCollapseState,
  HoveredPointPayload,
  PlatformListSortDirectionPayload,
} from './reducer';
import { SatelliteInFov } from '../../shared/entities/terminal';
import { ResourceFilter } from '../../components/common/table/types';
import { RootState } from '..';
import Antenna from '../../shared/entities/antenna';
import MissionPlan from '../../shared/entities/missionPlan';
import FlightLeg from '../../shared/entities/flightLeg';
import { PlatformListGroup } from '../../shared/entities/platformListGroup';

export const ActionCreators = {
  SetFleetViewAntenna: new ActionCreator<'SetFleetViewAntenna', any>('SetFleetViewAntenna'),
  SetFullTailViewAntenna: new ActionCreator<'SetFullTailViewAntenna', any>('SetFullTailViewAntenna'),
  SetExportAntenna: new ActionCreator<'SetExportAntenna', any>('SetExportAntenna'),

  ShowTerminalsInYourBeamPanel: new ActionCreator<'ShowTerminalsInYourBeamPanel', any>('ShowTerminalsInYourBeamPanel'),
  HideTerminalsInYourBeamPanel: new ActionCreator<'HideTerminalsInYourBeamPanel', any>('HideTerminalsInYourBeamPanel'),

  OpenDetailsPanel: new ActionCreator<'OpenDetailsPanel', any>('OpenDetailsPanel'),
  CloseDetailsPanel: new ActionCreator<'CloseDetailsPanel', any>('CloseDetailsPanel'),

  SetDetailsPanelGroupCollapseState: new ActionCreator<'SetDetailsPanelGroupCollapseState', any>(
    'SetDetailsPanelGroupCollapseState',
  ),

  setActiveFlightPath: new ActionCreator<'setActiveFlightPath', any>('setActiveFlightPath'),

  setHoveredAlternativeSatelliteInFov: new ActionCreator<'setHoveredAlternativeSatelliteInFov', any>(
    'setHoveredAlternativeSatelliteInFov',
  ),

  collapseAlternativeSatellitesInFov: new ActionCreator<'collapseAlternativeSatellitesInFov', any>(
    'collapseAlternativeSatellitesInFov',
  ),

  expandAlternativeSatellitesInFov: new ActionCreator<'expandAlternativeSatellitesInFov', any>(
    'expandAlternativeSatellitesInFov',
  ),

  SetHoveredBeam: new ActionCreator<'SetHoveredBeam', string | null>('SetHoveredBeam'),
  ShowTerminalInYourBeam: new ActionCreator<'ShowTerminalInYourBeam', any>('ShowTerminalInYourBeam'),
  HideTerminalInYourBeam: new ActionCreator<'HideTerminalInYourBeam', any>('HideTerminalInYourBeam'),
  ToggleTerminalsInYourBeamPanel: new ActionCreator<'ToggleTerminalsInYourBeamPanel', any>(
    'ToggleTerminalsInYourBeamPanel',
  ),
  SetTerminalsInYourBeamListSortField: new ActionCreator<'SetTerminalsInYourBeamListSortField', any>(
    'SetTerminalsInYourBeamListSortField',
  ),
  SetTerminalsYourBeamListSortDirection: new ActionCreator<'SetTerminalsYourBeamListSortDirection', any>(
    'SetTerminalsYourBeamListSortDirection',
  ),
  ShowAllTerminalsInYourBeam: new ActionCreator<'ShowAllTerminalsInYourBeam', any>('ShowAllTerminalsInYourBeam'),
  HideTerminalsInYourBeam: new ActionCreator<'HideTerminalsInYourBeam', any>('HideTerminalsInYourBeam'),

  ShowMissionOverlayMissionListModal: new ActionCreator<'ShowMissionOverlayMissionListModal', any>(
    'ShowMissionOverlayMissionListModal',
  ),
  HideMissionOverlayMissionListModal: new ActionCreator<'HideMissionOverlayMissionListModal', any>(
    'HideMissionOverlayMissionListModal',
  ),

  ShowMissionOverlayMissionListInModal: new ActionCreator<'ShowMissionOverlayMissionListInModal', any>(
    'ShowMissionOverlayMissionListInModal',
  ),
  HideMissionOverlayMissionListInModal: new ActionCreator<'HideMissionOverlayMissionListInModal', any>(
    'HideMissionOverlayMissionListInModal',
  ),

  ShowMissionOverlayFlightLegListInModal: new ActionCreator<'ShowMissionOverlayFlightLegListInModal', any>(
    'ShowMissionOverlayFlightLegListInModal',
  ),
  HideMissionOverlayFlightLegListInModal: new ActionCreator<'HideMissionOverlayFlightLegListInModal', any>(
    'HideMissionOverlayFlightLegListInModal',
  ),

  SetPrintMode: new ActionCreator<'SetPrintMode', boolean>('SetPrintMode'),
  UnsetPrintMode: new ActionCreator<'UnsetPrintMode', boolean>('UnsetPrintMode'),
  SetPrintModeAndPrint: new ActionCreator<'SetPrintModeAndPrint', any>('SetPrintModeAndPrint'),
  AfterSetPrintModeAndPrint: new ActionCreator<'AfterSetPrintModeAndPrint', any>('AfterSetPrintModeAndPrint'),

  SetHoveredAnnotation: new ActionCreator<'SetHoveredAnnotation', HoveredPointPayload>('SetHoveredAnnotation'),

  CollapseTerminalInBeamListItem: new ActionCreator<'CollapseTerminalInBeamListItem', string>(
    'CollapseTerminalInBeamListItem',
  ),
  ExpandTerminalInBeamListItem: new ActionCreator<'ExpandTerminalInBeamListItem', string>(
    'ExpandTerminalInBeamListItem',
  ),
  SetFtvTerminalsInYourBeamCollapsedTerminals: new ActionCreator<
    'SetFtvTerminalsInYourBeamCollapsedTerminals',
    { [datapathUuid: string]: boolean }
  >('SetFtvTerminalsInYourBeamCollapsedTerminals'),

  SetFleetViewMapCenterAndZoom: new ActionCreator<
    'SetFleetViewMapCenterAndZoom',
    { center: google.maps.LatLng; zoom: number }
  >('SetFleetViewMapCenterAndZoom'),

  SetFTVShouldZoomToFlightPath: new ActionCreator<'SetFTVShouldZoomToFlightPath', boolean>(
    'SetFTVShouldZoomToFlightPath',
  ),

  SetTooltipStepMap: new ActionCreator<'SetTooltipStepMap', { [key: number]: number }>('SetTooltipStepMap'),

  SetMissionOverlayMissionsSortField: new ActionCreator<'SetMissionOverlayMissionsSortField', keyof MissionPlan>(
    'SetMissionOverlayMissionsSortField',
  ),
  SetMissionOverlayMissionsSortDirection: new ActionCreator<'SetMissionOverlayMissionsSortDirection', 'asc' | 'desc'>(
    'SetMissionOverlayMissionsSortDirection',
  ),
  SetMissionOverlayFlightLegsSortField: new ActionCreator<'SetMissionOverlayFlightLegsSortField', keyof FlightLeg>(
    'SetMissionOverlayFlightLegsSortField',
  ),
  SetMissionOverlayFlightLegsSortDirection: new ActionCreator<
    'SetMissionOverlayFlightLegsSortDirection',
    'asc' | 'desc'
  >('SetMissionOverlayFlightLegsSortDirection'),
  SetExportViewTitle: new ActionCreator<'SetExportViewTitle', string>('SetExportViewTitle'),
  ClearExportViewTitle: new ActionCreator<'ClearExportViewTitle', undefined>('ClearExportViewTitle'),

  // Overlays Context UI
  OpenPlatformListDrawer: new ActionCreator<'OpenPlatformListDrawer', any>('OpenPlatformListDrawer'),
  ClosePlatformListDrawer: new ActionCreator<'ClosePlatformListDrawer', any>('ClosePlatformListDrawer'),
  SetPlatformListSortField: new ActionCreator<'SetPlatformListSortField', any>('SetPlatformListSortField'),
  SetPlatformListSortDirection: new ActionCreator<'SetPlatformListSortDirection', PlatformListSortDirectionPayload>(
    'SetPlatformListSortDirection',
  ),
  SetCollapsedTerminalsInPlatformList: new ActionCreator<
    'SetCollapsedTerminalsInPlatformList',
    { [datapathUuid: string]: boolean }
  >('SetCollapsedTerminalsInPlatformList'),
  CollapseTerminalInPlatformList: new ActionCreator<'CollapseTerminalInPlatformList', string>(
    'CollapseTerminalInPlatformList',
  ),
  ExpandTerminalInPlatformList: new ActionCreator<'ExpandTerminalInPlatformList', string>(
    'ExpandTerminalInPlatformList',
  ),
  ShowTerminalInPlatformList: new ActionCreator<'ShowTerminalInPlatformList', string>('ShowTerminalInPlatformList'),
  ShowTerminalsInPlatformList: new ActionCreator<'ShowTerminalsInPlatformList', string[]>(
    'ShowTerminalsInPlatformList',
  ),
  HideTerminalInPlatformList: new ActionCreator<'HideTerminalInPlatformList', string>('HideTerminalInPlatformList'),
  HideTerminalsInPlatformList: new ActionCreator<'HideTerminalsInPlatformList', string[]>(
    'HideTerminalsInPlatformList',
  ),
  HideAllTerminalsInPlatformList: new ActionCreator<'HideAllTerminalsInPlatformList', string[]>(
    'HideAllTerminalsInPlatformList',
  ),
  ShowAllTerminalsInPlatformList: new ActionCreator<'ShowAllTerminalsInPlatformList', void>(
    'ShowAllTerminalsInPlatformList',
  ),
  SetSelectedDatapathUuidInPlatformList: new ActionCreator<'SetSelectedDatapathUuidInPlatformList', any>(
    'SetSelectedDatapathUuidInPlatformList',
  ),
  ClearSelectedDatapathUuidInPlatformList: new ActionCreator<'ClearSelectedDatapathUuidInPlatformList', any>(
    'ClearSelectedDatapathUuidInPlatformList',
  ),
  SetSelectedTerminalTabIndex: new ActionCreator<'SetSelectedTerminalTabIndex', number | undefined>(
    'SetSelectedTerminalTabIndex',
  ),
  ClearSelectedTerminalTabIndex: new ActionCreator<'ClearSelectedTerminalTabIndex', void>(
    'ClearSelectedTerminalTabIndex',
  ),
  SetTerminalFilters: new ActionCreator<'SetTerminalFilters', ResourceFilter[]>('SetTerminalFilters'),
};

export type Action = typeof ActionCreators[keyof typeof ActionCreators];

export function setFleetViewAntenna(antenna: Antenna) {
  return ActionCreators.SetFleetViewAntenna.create(antenna);
}

export function setFullTailViewAntenna(antenna: Antenna) {
  return ActionCreators.SetFullTailViewAntenna.create(antenna);
}

export function setExportAntenna(antenna: Antenna) {
  return ActionCreators.SetExportAntenna.create(antenna);
}

export function showTerminalsInYourBeamPanel() {
  return ActionCreators.ShowTerminalsInYourBeamPanel.create();
}

export function hideTerminalsInYourBeamPanel() {
  return ActionCreators.HideTerminalsInYourBeamPanel.create();
}

export function toggleTerminalsInYourBeamPanel() {
  return ActionCreators.ToggleTerminalsInYourBeamPanel.create();
}

export function openDetailsPanel() {
  return ActionCreators.OpenDetailsPanel.create();
}

export function closeDetailsPanel() {
  return ActionCreators.CloseDetailsPanel.create();
}

export function setDetailsPanelGroupCollapseState(detailsPanelGroupCollapseState: DetailsPanelGroupCollapseState) {
  return ActionCreators.SetDetailsPanelGroupCollapseState.create(detailsPanelGroupCollapseState);
}

export function setHoveredAlternativeSatelliteInFov(satelliteInFov: SatelliteInFov) {
  return ActionCreators.setHoveredAlternativeSatelliteInFov.create(satelliteInFov);
}

export function clearHoveredAlternativeSatelliteInFov() {
  return ActionCreators.setHoveredAlternativeSatelliteInFov.create(null);
}

export function collapseAlternativeSatellitesInFov() {
  return ActionCreators.collapseAlternativeSatellitesInFov.create();
}

export function expandAlternativeSatellitesInFov() {
  return ActionCreators.expandAlternativeSatellitesInFov.create();
}

export function setHoveredBeam(beamName: string | null) {
  return ActionCreators.SetHoveredBeam.create(beamName);
}

export function showTerminalInYourBeam(datapathUuid: string) {
  return ActionCreators.ShowTerminalInYourBeam.create(datapathUuid);
}

export function hideTerminalInYourBeam(datapathUuid: string) {
  return ActionCreators.HideTerminalInYourBeam.create(datapathUuid);
}

export function hideTerminalsInYourBeam(datapathUuids: string[]) {
  return ActionCreators.HideTerminalsInYourBeam.create(datapathUuids);
}

export function showAllTerminalsInYourBeam() {
  return ActionCreators.ShowAllTerminalsInYourBeam.create();
}

export function setTerminalsYourBeamListSortDirection(direction: SortDirection) {
  return ActionCreators.SetTerminalsYourBeamListSortDirection.create(direction);
}

export function setTerminalsInYourBeamListSortField(field: string) {
  return ActionCreators.SetTerminalsInYourBeamListSortField.create(field);
}

export function showMissionOverlayMissionListModal() {
  return ActionCreators.ShowMissionOverlayMissionListModal.create();
}
export function hideMissionOverlayMissionListModal() {
  return ActionCreators.HideMissionOverlayMissionListModal.create();
}
export function showMissionOverlayMissionListInModal() {
  return ActionCreators.ShowMissionOverlayMissionListInModal.create();
}
export function hideMissionOverlayMissionListInModal() {
  return ActionCreators.HideMissionOverlayMissionListInModal.create();
}

export function showMissionOverlayFlightLegListInModal() {
  return ActionCreators.ShowMissionOverlayFlightLegListInModal.create();
}
export function hideMissionOverlayFlightLegListInModal() {
  return ActionCreators.HideMissionOverlayFlightLegListInModal.create();
}

export function setPrintMode() {
  return ActionCreators.SetPrintMode.create();
}

export function setPrintModeAndPrint() {
  return (dispatch: (arg0: { type: 'SetPrintModeAndPrint'; payload: any; meta: undefined }) => void) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(ActionCreators.SetPrintModeAndPrint.create());

      resolve();
    });
  };
}
export function unsetPrintMode() {
  return ActionCreators.UnsetPrintMode.create();
}

export function setFleetViewMapCenterAndZoom(center: google.maps.LatLng, zoom: number) {
  return (dispatch: any) => {
    dispatch(ActionCreators.SetFleetViewMapCenterAndZoom.create({ center, zoom }));
  };
}

export function setFTVShouldZoomToFlightPath(shouldZoom: boolean) {
  return ActionCreators.SetFTVShouldZoomToFlightPath.create(shouldZoom);
}

export function setFtvTerminalsInYourBeamCollapsedTerminals(fleetViewCollapsedTerminals: {
  [datapathUuid: string]: boolean;
}) {
  return ActionCreators.SetFtvTerminalsInYourBeamCollapsedTerminals.create(fleetViewCollapsedTerminals);
}

export function collapseTerminalInBeamListItem(datapathUuid: string) {
  return ActionCreators.CollapseTerminalInBeamListItem.create(datapathUuid);
}

export function expandTerminalInBeamListItem(datapathUuid: string) {
  return ActionCreators.ExpandTerminalInBeamListItem.create(datapathUuid);
}

export function setHoveredAnnotation(payload: HoveredPointPayload) {
  return ActionCreators.SetHoveredAnnotation.create(payload);
}

export function setTooltipStepMap(payload: { [key: number]: number }) {
  return ActionCreators.SetTooltipStepMap.create(payload);
}

export function setMissionOverlayMissionsSortField(sortField: keyof MissionPlan) {
  return ActionCreators.SetMissionOverlayMissionsSortField.create(sortField);
}

export function setMissionOverlayMissionsSortDirection(direction: 'asc' | 'desc') {
  return ActionCreators.SetMissionOverlayMissionsSortDirection.create(direction);
}

export function setMissionOverlayFlightLegsSortField(sortField: keyof FlightLeg) {
  return ActionCreators.SetMissionOverlayFlightLegsSortField.create(sortField);
}

export function setMissionOverlayFlightLegsSortDirection(direction: 'asc' | 'desc') {
  return ActionCreators.SetMissionOverlayFlightLegsSortDirection.create(direction);
}

export function setExportViewTitle(missionTitle: string) {
  return ActionCreators.SetExportViewTitle.create(missionTitle);
}

export function clearExportViewTitle() {
  return ActionCreators.ClearExportViewTitle.create();
}

/**
 * Overlays Context UI
 */
export function openPlatformListDrawer() {
  return ActionCreators.OpenPlatformListDrawer.create();
}

export function closePlatformListDrawer() {
  return ActionCreators.ClosePlatformListDrawer.create();
}

export function setPlatformListSortField(field: string) {
  return ActionCreators.SetPlatformListSortField.create(field);
}

export function setPlatformListSortDirection(groupName: PlatformListGroup, direction: SortDirection) {
  return ActionCreators.SetPlatformListSortDirection.create({
    groupName: groupName as PlatformListGroup,
    direction: direction,
  });
}

export function setSelectedDatapathUuidInPlatformList(datapathUuid: string) {
  return ActionCreators.SetSelectedDatapathUuidInPlatformList.create(datapathUuid);
}

export function clearSelectedDatapathUuidInPlatformList() {
  return ActionCreators.ClearSelectedDatapathUuidInPlatformList.create();
}

export function toggleTerminalFilters(filter: ResourceFilter) {
  return (dispatch: any, getState: () => RootState) => {
    const oldFilters =
      getState().ui.overlaysContextUiState[getState().ui.overlaysCurrentContext].terminalFilters.slice();
    const filterExistedIndex = oldFilters.findIndex((oldFilter: ResourceFilter) => {
      return isEqual(oldFilter, filter);
    });

    if (filterExistedIndex >= 0) {
      oldFilters.splice(filterExistedIndex, 1);
      dispatch(ActionCreators.SetTerminalFilters.create(oldFilters));
    } else {
      const filters = uniqBy([filter, ...oldFilters], 'key').reverse();
      dispatch(ActionCreators.SetTerminalFilters.create(filters));
    }
  };
}

export function showTerminalInPlatformList(datapathUuid: string) {
  return ActionCreators.ShowTerminalInPlatformList.create(datapathUuid);
}

export function showTerminalsInPlatformList(datapathUuids: string[]) {
  return ActionCreators.ShowTerminalsInPlatformList.create(datapathUuids);
}

export function hideTerminalInPlatformList(datapathUuid: string) {
  return ActionCreators.HideTerminalInPlatformList.create(datapathUuid);
}

export function hideTerminalsInPlatformList(datapathUuids: string[]) {
  return ActionCreators.HideTerminalsInPlatformList.create(datapathUuids);
}

export function hideAllTerminalsInPlatformList() {
  return (dispatch: any, getState: () => RootState) => {
    dispatch(ActionCreators.HideAllTerminalsInPlatformList.create(Object.keys(getState().terminals.terminals)));
  };
}

export function showAllTerminalsInPlatformList() {
  return ActionCreators.ShowAllTerminalsInPlatformList.create();
}

export function setCollapsedTerminalsInPlatformList(terminalListCollapseState: { [datapathUuid: string]: boolean }) {
  return ActionCreators.SetCollapsedTerminalsInPlatformList.create(terminalListCollapseState);
}

export function collapseTerminalInPlatformList(datapathUuid: string) {
  return ActionCreators.CollapseTerminalInPlatformList.create(datapathUuid);
}

export function expandTerminalInPlatformList(datapathUuid: string) {
  return ActionCreators.ExpandTerminalInPlatformList.create(datapathUuid);
}

export function setSelectedTerminalTabIndex(tabIndex: number | undefined) {
  return ActionCreators.SetSelectedTerminalTabIndex.create(tabIndex);
}

export function clearSelectedTerminalTabIndex() {
  return ActionCreators.ClearSelectedTerminalTabIndex.create();
}
